import React, { useState, useEffect } from 'react';
import apiClient from '../../api/client';
import './RawPicture.css';

const RawPicture = () => {
  const [pictures, setPictures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedPictures, setSelectedPictures] = useState(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());
  const [showTokenExpiryWarning, setShowTokenExpiryWarning] = useState(false);

  // Fetch pictures when both date filters are selected
  useEffect(() => {
    if (startDate && endDate) {
      fetchPictures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchPictures = async () => {
    // Only fetch if both date filters are selected
    if (!startDate || !endDate) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setImageLoadErrors(new Set()); // Clear previous image errors
    setShowTokenExpiryWarning(false); // Clear warning
    
    try {
      const allPictures = [];

      // Use time range API endpoint - both dates are required
      const start = new Date(startDate).toISOString();
      const end = new Date(endDate + 'T23:59:59').toISOString();
      
      const response = await apiClient.getUploadSessionsByTimeRange(start, end);
      
      // Process sessions response
      if (response.sessions && Array.isArray(response.sessions)) {
        response.sessions.forEach((session) => {
          const rawPictures = session.raw_pictures || [];
          
          rawPictures.forEach((picture) => {
            allPictures.push({
              ...picture,
              sessionId: session.id,
              reservationId: session.reservation_id,
              vehicleProfileId: session.vehicle_profile_id,
              uploaderId: session.uploader_id,
              sessionStatus: session.status,
              sessionCreatedAt: session.created_at,
              shotAt: session.shot_at,
              // Map to consistent property names
              url: picture.sas_url,
              name: picture.filename,
              make: picture.cameraMake,
              model: picture.cameraModel,
              createdDate: picture.shootingTime || session.created_at
            });
          });
        });
      }
      
      // Filenames can repeat across reservations; deduplicate only within a session.
      const uniquePicturesMap = new Map();
      allPictures.forEach((picture) => {
        const key = JSON.stringify([picture.sessionId, picture.url]);
        if (!uniquePicturesMap.has(key)) uniquePicturesMap.set(key, picture);
      });
      
      // Convert Map back to array
      const uniquePictures = Array.from(uniquePicturesMap.values());
      
      // Sort pictures by shooting time (oldest first)
      uniquePictures.sort((a, b) => {
        const timeA = new Date(a.createdDate || 0).getTime();
        const timeB = new Date(b.createdDate || 0).getTime();
        return timeA - timeB;
      });
      
      setPictures(uniquePictures);
    } catch (err) {
      console.error('Error fetching pictures:', err);
      setError('Failed to load pictures. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePictureClick = (picture) => {
    setSelectedPicture(picture);
  };

  const handleCloseModal = () => {
    setSelectedPicture(null);
  };

  const handleImageError = (pictureUrl) => {
    setImageLoadErrors(prev => new Set(prev).add(pictureUrl));
    
    // Show warning if multiple images fail to load (likely token expiry)
    if (imageLoadErrors.size >= 3) {
      setShowTokenExpiryWarning(true);
    }
  };

  const handleImageLoad = (pictureUrl) => {
    // Remove from error set if image loads successfully
    setImageLoadErrors(prev => {
      const newSet = new Set(prev);
      newSet.delete(pictureUrl);
      return newSet;
    });
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
    setSelectedMake('');
    setSelectedModel('');
  };

  const handleSelectPicture = (pictureUrl, e) => {
    e.stopPropagation();
    setSelectedPictures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(pictureUrl)) {
        newSet.delete(pictureUrl);
      } else {
        newSet.add(pictureUrl);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedPictures.size === filteredPictures.length) {
      setSelectedPictures(new Set());
    } else {
      setSelectedPictures(new Set(filteredPictures.map(p => p.url)));
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedPictures.size === 0) return;

    setIsDownloading(true);
    try {
      const picturesToDownload = filteredPictures.filter(p => selectedPictures.has(p.url));
      
      for (let i = 0; i < picturesToDownload.length; i++) {
        const picture = picturesToDownload[i];
        try {
          let blob = null;

          // Option A: Try backend download endpoint first (server-side EXIF update)
          if (picture.id) {
            try {
              blob = await apiClient.downloadPictureBlob(picture.id);
            } catch (backendErr) {
              console.warn(`Backend download endpoint failed for picture ${picture.id}, falling back to direct URL:`, backendErr.message);
            }
          }

          if (!blob) {
            // Fetch the image
            const response = await fetch(picture.url);
            
            if (!response.ok) {
              console.error('Failed to fetch image:', response.status);
              continue;
            }
            
            blob = await response.blob();
          }
          
          // Check blob type
          if (!blob.type || !blob.type.startsWith('image/')) {
            console.warn('Fetched blob is not an image:', blob.type);
            blob = new Blob([blob], { type: 'image/jpeg' });
          }
          
          // Download with stagger
          await new Promise((resolve) => {
            setTimeout(() => {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = picture.name || `picture_${i + 1}.jpg`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              resolve();
            }, i * 200); // Stagger by 200ms
          });
        } catch (error) {
          console.error(`Failed to download ${picture.name}:`, error);
        }
      }
      
      // Clear selection after download
      setSelectedPictures(new Set());
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download pictures. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Extract unique makes and models from pictures
  const uniqueMakes = [...new Set(pictures.map(p => p.make).filter(Boolean))].sort();
  const uniqueModels = [...new Set(
    pictures
      .filter(p => !selectedMake || p.make === selectedMake)
      .map(p => p.model)
      .filter(Boolean)
  )].sort();

  // Filter pictures based on search query, date range, make, and model
  const filteredPictures = pictures.filter(picture => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        picture.make?.toLowerCase().includes(query) ||
        picture.model?.toLowerCase().includes(query) ||
        picture.name?.toLowerCase().includes(query) ||
        picture.uploaderId?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Date range filter
    if (startDate || endDate) {
      if (!picture.createdDate) return false;
      
      const pictureDate = new Date(picture.createdDate);
      pictureDate.setHours(0, 0, 0, 0); // Reset time for date-only comparison
      
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (pictureDate < start) return false;
      }
      
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // End of day
        if (pictureDate > end) return false;
      }
    }

    // Make filter
    if (selectedMake && picture.make !== selectedMake) {
      return false;
    }

    // Model filter
    if (selectedModel && picture.model !== selectedModel) {
      return false;
    }

    return true;
  });

  return (
    <div className="raw-picture-page">
      <div className="page-header">
        <div className="main-title">Raw Pictures</div>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search by filename, make, model, uploader..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="refresh-btn" onClick={fetchPictures} disabled={isLoading}>
            {isLoading ? '↻ Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showTokenExpiryWarning && (
        <div className="warning-message">
          <strong>⚠️ Images may have expired</strong>
          <p>Some images failed to load. This may be because the access tokens have expired. Click the refresh button to reload the images.</p>
          <button 
            className="refresh-warning-btn" 
            onClick={fetchPictures}
            disabled={isLoading}
          >
            ↻ Refresh Images
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-section">
          <div className="filter-label">Filters:</div>
          <div className="filter-inputs">
            <div className="date-input-group">
              <label htmlFor="start-date" className="input-label">From:</label>
              <input
                id="start-date"
                type="date"
                className="date-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
              />
            </div>
            <div className="date-input-group">
              <label htmlFor="end-date" className="input-label">To:</label>
              <input
                id="end-date"
                type="date"
                className="date-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
              />
            </div>
            <div className="select-input-group">
              <label htmlFor="make-select" className="input-label">Make:</label>
              <select
                id="make-select"
                className="filter-select"
                value={selectedMake}
                onChange={(e) => {
                  setSelectedMake(e.target.value);
                  setSelectedModel(''); // Reset model when make changes
                }}
              >
                <option value="">All Makes</option>
                {uniqueMakes.map(make => (
                  <option key={make} value={make}>{make}</option>
                ))}
              </select>
            </div>
            <div className="select-input-group">
              <label htmlFor="model-select" className="input-label">Model:</label>
              <select
                id="model-select"
                className="filter-select"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={!selectedMake}
              >
                <option value="">All Models</option>
                {uniqueModels.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="filter-actions">
          {selectedPictures.size > 0 && (
            <button 
              className="download-selected-btn" 
              onClick={handleDownloadSelected}
              disabled={isDownloading}
            >
              {isDownloading ? '⬇ Downloading...' : `⬇ Download (${selectedPictures.size})`}
            </button>
          )}
          {(startDate || endDate || searchQuery || selectedMake || selectedModel) && (
            <button className="clear-filters-btn" onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="raw-picture-content">
        {!startDate || !endDate ? (
          <div className="empty-state">
            <div className="empty-icon">📅</div>
            <div className="empty-text">Select both start and end dates to view pictures</div>
            <div className="empty-subtext">
              {!startDate && !endDate ? 'Please select both start and end dates in the filters above' :
               !startDate ? 'Please select a start date' :
               'Please select an end date'}
            </div>
          </div>
        ) : isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div>Loading pictures...</div>
          </div>
        ) : filteredPictures.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📷</div>
            <div className="empty-text">
              {searchQuery ? 'No matching pictures found' : 'No pictures available'}
            </div>
            <div className="empty-subtext">
              {searchQuery ? 'Try a different search query' : 'No pictures found in the selected time range'}
            </div>
          </div>
        ) : (
          <>
            <div className="pictures-stats">
              <span className="stats-text">
                Showing {filteredPictures.length} of {pictures.length} pictures
              </span>
              <label className="select-all-checkbox">
                <input
                  type="checkbox"
                  checked={selectedPictures.size === filteredPictures.length && filteredPictures.length > 0}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
              </label>
            </div>
            <div className="pictures-grid">
              {filteredPictures.map((picture, index) => {
                const isSelected = selectedPictures.has(picture.url);
                const hasError = imageLoadErrors.has(picture.url);
                // Use a unique key combining session ID and filename/index
                const uniqueKey = `${picture.sessionId}-${picture.name}-${index}`;
                return (
                  <div key={uniqueKey} className={`picture-card ${isSelected ? 'selected' : ''} ${hasError ? 'error' : ''}`}>
                    <div 
                      className="card-image-container"
                      onClick={() => handlePictureClick(picture)}
                    >
                      <div className="selection-overlay">
                        <input
                          type="checkbox"
                          className="picture-checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectPicture(picture.url, e)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {picture.url ? (
                        <>
                          <img 
                            src={picture.url} 
                            alt={picture.name || `Picture ${index + 1}`}
                            className="card-image"
                            loading="lazy"
                            onError={() => handleImageError(picture.url)}
                            onLoad={() => handleImageLoad(picture.url)}
                          />
                          {hasError && (
                            <div className="image-error-overlay">
                              <span>⚠️</span>
                              <span>Failed to load</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="image-placeholder">
                          <span>📷</span>
                          <span>No Preview</span>
                        </div>
                      )}
                    </div>
                    <div className="card-info">
                      <div className="card-title">
                        {picture.name || 'Unknown File'}
                      </div>
                      {(picture.make || picture.model) && (
                        <div className="card-detail">
                          <span className="detail-icon">📱</span>
                          <span>{[picture.make, picture.model].filter(Boolean).join(' ')}</span>
                        </div>
                      )}
                      {picture.createdDate && (
                        <div className="card-detail">
                          <span className="detail-icon">📅</span>
                          <span>
                            {new Date(picture.createdDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      {picture.uploaderId && (
                        <div className="card-detail">
                          <span className="detail-icon">👤</span>
                          <span>{picture.uploaderId}</span>
                        </div>
                      )}
                      {picture.size && (
                        <div className="card-detail">
                          <span className="detail-icon">💾</span>
                          <span>{(picture.size / 1024).toFixed(1)} KB</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Image Modal */}
      {selectedPicture && (
        <div className="picture-modal" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>✕</button>
            {selectedPicture.url ? (
              <img 
                src={selectedPicture.url}
                alt={selectedPicture.name}
                className="modal-image"
              />
            ) : (
              <div className="image-placeholder" style={{ height: '400px' }}>
                <span>📷</span>
                <span>No Preview Available</span>
              </div>
            )}
            <div className="modal-info">
              <h3>{selectedPicture.name || 'Unknown File'}</h3>
              {(selectedPicture.make || selectedPicture.model) && (
                <p>Camera: {[selectedPicture.make, selectedPicture.model].filter(Boolean).join(' ')}</p>
              )}
              {selectedPicture.uploaderId && <p>Uploader: {selectedPicture.uploaderId}</p>}
              {selectedPicture.createdDate && (
                <p>Shot at: {new Date(selectedPicture.createdDate).toLocaleString()}</p>
              )}
              {selectedPicture.sessionStatus && (
                <p>Session Status: <span style={{ 
                  textTransform: 'uppercase', 
                  fontWeight: 'bold',
                  color: selectedPicture.sessionStatus === 'completed' ? '#059669' : '#dc2626'
                }}>{selectedPicture.sessionStatus}</span></p>
              )}
              {selectedPicture.size && <p>Size: {(selectedPicture.size / 1024).toFixed(1)} KB</p>}
              {selectedPicture.type && <p>Type: {selectedPicture.type}</p>}
              {selectedPicture.gpsLatitude && selectedPicture.gpsLongitude && (
                <p>GPS: {selectedPicture.gpsLatitude}, {selectedPicture.gpsLongitude}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RawPicture;
